import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Row, Col, Card, CardBody } from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { getPmSessionMonthRangeStrings } from "helpers/pmSessionDateHelper";
import { getCptAnalyticsTimezone } from "../common/cptAnalyticsTimezone";
import IntegrationFilter from "./IntegrationFilter";
import CommonPatientListModal from "./CommonPatientListModal";
import PatientListStatusButtons from "./PatientListStatusButtons";
import CPTCodePerformanceShimmer from "./CPTCodePerformanceShimmer";
import {
  CPT_DETAILS_LAYOUT_CCM_CLINICAL,
  CPT_DETAILS_LAYOUT_RPM_CLINICAL,
  CPT_DETAILS_LAYOUT_TECHNICAL
} from "./PatientListTable";
import CptDashboardSkeletonOverlay from "../common/CptDashboardSkeletonOverlay";
import "./css/CPTCodesDashboard.css";

const CPT_CODE_PERFORMANCE_FILTER_KEY = "cptCodePerformanceFilter";
const RPM_CLINICAL_COMBINED_MET_CODES = ["99470", "99457"];
const RPM_CLINICAL_DISPLAY_CPT_CODES = ["99470", "99457", "99458"];
const CCM_CLINICAL_DISPLAY_CPT_CODES = ["99490", "99439"];
const TECHNICAL_DISPLAY_CPT_CODES = ["99445", "99454"];
const COMPONENT_POPUP_CPT_CODES = {
  technical: TECHNICAL_DISPLAY_CPT_CODES,
  professional: RPM_CLINICAL_DISPLAY_CPT_CODES,
  ccmClinical: CCM_CLINICAL_DISPLAY_CPT_CODES
};

const resolveComponentPopupCptCodes = (componentKey, componentCptCodes = []) => {
  if (COMPONENT_POPUP_CPT_CODES[componentKey]) {
    return COMPONENT_POPUP_CPT_CODES[componentKey];
  }
  return Array.isArray(componentCptCodes) ? componentCptCodes.filter(Boolean) : [];
};

const CPT_PERFORMANCE_CODE_META = {
  99470: { title: "RPM Clinical Service" },
  99457: { title: "RPM Interactive Communication (20 min)" },
  99458: { title: "RPM Additional Treatment Management (20 min)" }
};

const STATUS_COLORS = {
  good: { bg: "#d1fae5", text: "#059669", label: "Good" },
  warning: { bg: "#fef3c7", text: "#d97706", label: "Warning" },
  critical: { bg: "#fee2e2", text: "#dc2626", label: "Critical" }
};

const CHART_COLORS = [
  "#0ea5e9",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#6366f1"
];

const COLORS = {
  green: "#10b981",
  red: "#ef4444",
  yellow: "#f59e0b",
  blue: "#0ea5e9",
  purple: "#8b5cf6"
};

const getStatusColor = (percentage) => {
  if (percentage >= 70) return "#10b981";
  if (percentage >= 30) return "#f59e0b";
  return "#ef4444";
};

const getSavedFilter = () => {
  try {
    const savedFilter = sessionStorage.getItem(CPT_CODE_PERFORMANCE_FILTER_KEY);
    return savedFilter ? JSON.parse(savedFilter) : null;
  } catch (error) {
    return null;
  }
};

const saveFilterToStorage = (filterState) => {
  try {
    sessionStorage.setItem(CPT_CODE_PERFORMANCE_FILTER_KEY, JSON.stringify({
      selectedMonth: filterState.selectedMonth,
      selectedYear: filterState.selectedYear
    }));
  } catch (error) {
  }
};

const CPTCodePerformance = (props) => {
  const {
    dispatch,
    isActiveTab,
    cptCodeDashboardData,
    ongoingCptCodeDashboardRequest,
    cptCodePatientListData,
    ongoingCptCodePatientListRequest,
    componentPatientsData,
    ongoingComponentPatientsRequest
  } = props;

  const initFilter = () => {
    const currentMonth = moment().format("MM");
    const currentYear = moment().format("YYYY");
    const savedFilter = getSavedFilter();
    const sm = savedFilter?.selectedMonth || currentMonth;
    const sy = savedFilter?.selectedYear || currentYear;
    const { startDate: defaultStart, endDate: defaultEnd } = getPmSessionMonthRangeStrings(sy, sm);
    return {
      selectedMonth: sm,
      selectedYear: sy,
      dateRange: {
        startDate: defaultStart,
        endDate: defaultEnd,
        selectedMonth: sm,
        selectedYear: sy
      }
    };
  };

  const init = initFilter();
  const [selectedCPTCode, setSelectedCPTCode] = useState(null);
  const [selectedComponentKey, setSelectedComponentKey] = useState(null);
  const [selectedComponentCptCodes, setSelectedComponentCptCodes] = useState([]);
  const [patientListModal, setPatientListModal] = useState(false);
  const [patientListType, setPatientListType] = useState("met");
  const [isPatientListFetching, setIsPatientListFetching] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(init.selectedMonth);
  const [selectedYear, setSelectedYear] = useState(init.selectedYear);
  const [dateRange, setDateRange] = useState(init.dateRange);
  const [isFiltering, setIsFiltering] = useState(false);
  const [dataKey, setDataKey] = useState(0);

  const prevOngoingDashboard = useRef();
  const prevOngoingPatientList = useRef();
  const prevOngoingComponentPatients = useRef();

  useEffect(() => {
    if (prevOngoingDashboard.current === true && ongoingCptCodeDashboardRequest === false) {
      setIsFiltering(false);
      setDataKey((k) => k + 1);
    }
    prevOngoingDashboard.current = ongoingCptCodeDashboardRequest;
  }, [ongoingCptCodeDashboardRequest]);

  useEffect(() => {
    if (prevOngoingPatientList.current === true && ongoingCptCodePatientListRequest === false) {
      setIsPatientListFetching(false);
    }
    prevOngoingPatientList.current = ongoingCptCodePatientListRequest;
  }, [ongoingCptCodePatientListRequest]);

  useEffect(() => {
    if (prevOngoingComponentPatients.current === true && ongoingComponentPatientsRequest === false) {
      setIsPatientListFetching(false);
    }
    prevOngoingComponentPatients.current = ongoingComponentPatientsRequest;
  }, [ongoingComponentPatientsRequest]);

  useEffect(() => {
    return () => {
      try {
        sessionStorage.removeItem(CPT_CODE_PERFORMANCE_FILTER_KEY);
      } catch (error) {
        // Ignore storage cleanup errors.
      }
    };
  }, []);

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }
    setIsFiltering(true);
    dispatch({
      type: "DO_GET_CPT_CODE_DASHBOARD",
      payload: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        timezone: getCptAnalyticsTimezone()
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTab]);

  const getDisplayMonth = () => {
    const monthLabel = moment(`${selectedYear}-${selectedMonth}-01`).format("MMMM");
    return `${monthLabel} ${selectedYear}`;
  };

  const fetchData = (range) => {
    const params = {
      startDate: range?.startDate || dateRange.startDate,
      endDate: range?.endDate || dateRange.endDate,
      timezone: getCptAnalyticsTimezone()
    };
    setIsFiltering(true);
    dispatch({ type: "DO_GET_CPT_CODE_DASHBOARD", payload: params });
  };

  const handleFilterChange = (filterState) => {
    setSelectedMonth(filterState.selectedMonth);
    setSelectedYear(filterState.selectedYear);
    setDateRange(filterState);
  };

  const handleFilterApply = (filterState) => {
    saveFilterToStorage(filterState);
    setSelectedMonth(filterState.selectedMonth);
    setSelectedYear(filterState.selectedYear);
    setDateRange(filterState);
    fetchData(filterState);
  };

  const getStatus = (percentage) => {
    if (percentage >= 70) return STATUS_COLORS.good;
    if (percentage >= 30) return STATUS_COLORS.warning;
    return STATUS_COLORS.critical;
  };

  const getComponentMetrics = () => {
    const summary = cptCodeDashboardData?.summary || {};
    const componentSummary = cptCodeDashboardData?.summary?.componentSummary || {};
    const cptCodes = summary?.cptCodes || [];
    const totalPatients = summary?.totalPatients || 0;

    const cptMap = cptCodes.reduce((acc, item) => {
      const code = String(item?.code ?? "").trim();
      if (code) acc[code] = item;
      return acc;
    }, {});

    const getPerCodeCount = (code, field, service = {}) => {
      const normalizedCode = String(code).trim();
      const serviceFieldKey = field === "met" ? `${normalizedCode}Met` : `${normalizedCode}NotMet`;
      const serviceValue = service?.[serviceFieldKey];
      if (serviceValue !== null && serviceValue !== undefined && serviceValue !== "") {
        return Number(serviceValue) || 0;
      }
      const cptField = field === "met" ? "metCount" : "notMetCount";
      return Number(cptMap[normalizedCode]?.[cptField] || 0);
    };

    const aggregateCodes = (codes = []) => {
      const met = codes.reduce((sum, code) => sum + Number(cptMap[code]?.metCount || 0), 0);
      const notMet = codes.reduce((sum, code) => sum + Number(cptMap[code]?.notMetCount || 0), 0);
      const total = met + notMet;
      return {
        met,
        notMet,
        percentage: total > 0 ? Math.round((met / total) * 100) : 0
      };
    };

    const technicalFallback = aggregateCodes(["99445", "99454"]);
    const ccmClinicalFallback = aggregateCodes(["99490", "99439"]);
    const patientEducationFallback = aggregateCodes(["99453"]);

    const normalizeService = (service = {}, fallback = {}) => {
      const met = Number(service.metCount ?? service.met ?? fallback.met ?? 0);
      const notMet = Number(service.notMetCount ?? service.notMet ?? fallback.notMet ?? 0);
      const total = Number(service.total ?? (met + notMet));
      return {
        met,
        notMet,
        total,
        percentage: total > 0 ? Math.round((met / total) * 100) : 0
      };
    };

    const technical = summary?.rpmTechnicalServices
      ? normalizeService(summary.rpmTechnicalServices, technicalFallback)
      : normalizeService(componentSummary.technical, technicalFallback);
    const rpmClinicalService = summary?.rpmClinicalServices || {};
    const professional99470Met = getPerCodeCount("99470", "met", rpmClinicalService);
    const professional99457Met = getPerCodeCount("99457", "met", rpmClinicalService);
    const professional99458Met = getPerCodeCount("99458", "met", rpmClinicalService);
    const professionalMet = RPM_CLINICAL_COMBINED_MET_CODES.reduce(
      (sum, code) => sum + getPerCodeCount(code, "met", rpmClinicalService),
      0
    );
    const professionalNotMet = Number(
      rpmClinicalService.notMetCount ??
      rpmClinicalService.notMet ??
      componentSummary.professional?.notMetCount ??
      componentSummary.professional?.notMet ??
      0
    );
    const serviceTotal = rpmClinicalService.total;
    const professionalTotal =
      serviceTotal !== null && serviceTotal !== undefined && serviceTotal !== ""
        ? Number(serviceTotal)
        : Number(rpmClinicalService.metCount ?? rpmClinicalService.met ?? 0) + professionalNotMet;
    const professional = {
      met: professionalMet,
      notMet: professionalNotMet,
      total: professionalTotal,
      percentage: professionalTotal > 0 ? Math.round((professionalMet / professionalTotal) * 100) : 0
    };
    const ccmClinical = summary?.ccmClinicalServices
      ? normalizeService(summary.ccmClinicalServices, ccmClinicalFallback)
      : normalizeService(undefined, ccmClinicalFallback);
    const patientEducationSetup = normalizeService(componentSummary.patientEducationSetup, patientEducationFallback);
    
    const bothMet = componentSummary.bothMet || 0;
    const onlyTechnical = componentSummary.onlyTechnical || 0;
    const onlyProfessional = componentSummary.onlyProfessional || 0;
    const neither = componentSummary.neither || 0;
    
    const fullyBillable = summary.fullyBillable || 0;
    const partial = onlyTechnical + onlyProfessional;
    const notBillable = summary.notBillable || 0;
    
    return {
      totalPatients,
      fullyBillable,
      partial,
      notBillable,
      technical: technical.percentage || 0,
      professional: professional.percentage || 0,
      technicalMet: technical.met,
      technicalNotMet: technical.notMet,
      technicalTotal: technical.total,
      technical99445Met: Number(summary?.rpmTechnicalServices?.["99445Met"] || 0),
      technical99454Met: Number(summary?.rpmTechnicalServices?.["99454Met"] || 0),
      professionalMet,
      professionalNotMet,
      professionalTotal,
      professional99470Met,
      professional99457Met,
      professional99458Met,
      ccmClinicalMet: ccmClinical.met,
      ccmClinicalNotMet: ccmClinical.notMet,
      ccmClinicalTotal: ccmClinical.total,
      ccm99490Met: Number(summary?.ccmClinicalServices?.["99490Met"] || 0),
      patientEducationSetup: patientEducationSetup.percentage || 0,
      patientEducationSetupMet: patientEducationSetup.met || 0,
      patientEducationSetupNotMet: patientEducationSetup.notMet || 0,
      patientEducationSetupTotal: patientEducationSetup.total || 0,
      newEnrollmentCurrentMet: Number(summary?.newPatientEnrollmentsCompleted?.currentMonth?.metCount || 0),
      newEnrollmentPastMet: Number(summary?.newPatientEnrollmentsCompleted?.pastMonth?.metCount || 0),
      onlyTechnical,
      onlyProfessional,
      neither,
      fullyBillablePercentage: totalPatients > 0 ? Math.round((fullyBillable / totalPatients) * 100) : 0,
      notBillablePercentage: totalPatients > 0 ? Math.round((notBillable / totalPatients) * 100) : 0
    };
  };

  const getPatientListStatusLabel = () => {
    return patientListType === "met" ? "MET" : "NOT MET";
  };

  const getPatientListStatusQuery = (type = patientListType) => {
    return type === "met" ? "met" : "notMet";
  };

  const fetchPatientList = ({
    cptCode = selectedCPTCode,
    component = selectedComponentKey,
    componentCptCodes = selectedComponentCptCodes,
    type = patientListType,
    monthContext = null
  } = {}) => {
    const sanitizePayload = (payload = {}) =>
      Object.keys(payload).reduce((acc, key) => {
        const value = payload[key];
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          value === "null" ||
          value === "undefined"
        ) {
          return acc;
        }
        acc[key] = value;
        return acc;
      }, {});

    if (!cptCode && !component) {
      return;
    }

    if (component) {
      const popupCptCodes = resolveComponentPopupCptCodes(component, componentCptCodes);
      const payload = {
        mode: "component",
        componentType: component,
        status: getPatientListStatusQuery(type),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        timezone: getCptAnalyticsTimezone()
      };
      if (popupCptCodes.length > 0) {
        payload.cptCodes = popupCptCodes.join(",");
      }
      if (process.env.NODE_ENV !== "production") {
        // Debug helper for verifying combined component payload integration.
        // eslint-disable-next-line no-console
        console.log("[CPT Popup] Component payload:", sanitizePayload(payload));
      }

      dispatch({
        type: "DO_GET_CPT_CODE_PATIENT_LIST",
        payload: sanitizePayload(payload)
      });
      return;
    }

    const payload = {
      mode: "cpt",
      status: getPatientListStatusQuery(type),
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      timezone: getCptAnalyticsTimezone()
    };

    if (cptCode) {
      payload.cptCode = cptCode;
    }
    if (monthContext) {
      payload.monthContext = monthContext;
    }
    if (process.env.NODE_ENV !== "production") {
      // Debug helper for verifying single CPT payload integration.
      // eslint-disable-next-line no-console
      console.log("[CPT Popup] CPT payload:", sanitizePayload(payload));
    }

    dispatch({
      type: "DO_GET_CPT_CODE_PATIENT_LIST",
      payload: sanitizePayload(payload)
    });
  };

  const openPatientList = (cptCode, type, monthContext = null) => {
    setSelectedCPTCode(cptCode);
    setSelectedComponentKey(null);
    setSelectedComponentCptCodes([]);
    setPatientListModal(true);
    setPatientListType(type);
    setIsPatientListFetching(true);
    fetchPatientList({ cptCode, type, monthContext });
  };

  const openComponentPatientList = (componentKey, type, componentCptCodes = []) => {
    setSelectedCPTCode(null);
    setSelectedComponentKey(componentKey);
    setSelectedComponentCptCodes(Array.isArray(componentCptCodes) ? componentCptCodes : []);
    setPatientListModal(true);
    setPatientListType(type);
    setIsPatientListFetching(true);
    fetchPatientList({ component: componentKey, componentCptCodes, type });
  };

  const closePatientList = () => {
    setPatientListModal(false);
    setSelectedCPTCode(null);
    setSelectedComponentKey(null);
    setSelectedComponentCptCodes([]);
    setPatientListType("met");
    setIsPatientListFetching(false);
  };

  const getPatientDashboardPath = (patient = {}) => {
    const patientId = patient?._id || patient?.id || patient?.patientId || patient?.userId;
    if (!patientId) return "";

    const firstName = patient?.fname || patient?.firstName || "";
    const lastName = patient?.lname || patient?.lastName || "";
    const fallbackName = String(patient?.patientName || patient?.name || "");
    const fallbackParts = fallbackName.split(",").map((part) => part.trim());
    const resolvedLastName = lastName || fallbackParts[0] || "";
    const resolvedFirstName = firstName || fallbackParts[1] || "";
    const sanitize = (value) =>
      String(value || "")
        .replace(/\s+/g, "-")
        .replace(/[^a-zA-Z0-9-]/g, "")
        .toLowerCase();

    const slug = `${sanitize(resolvedFirstName)}-${sanitize(resolvedLastName)}`.replace(/^-|-$/g, "");
    return `/dashboard/${slug || "patient"}/${patientId}`;
  };

  const handleViewPatientProfile = (patient = {}) => {
    const path = getPatientDashboardPath(patient);
    if (!path) {
      toast.error("Patient profile is unavailable.");
      return;
    }
    window.location.href = path;
  };

  const getComponentCardConfigs = () => {
    const metrics = getComponentMetrics();
    const cptCodes = cptCodeDashboardData?.summary?.cptCodes || [];
    const patientEducationSetup = cptCodes.find((item) => item.code === "99453") || {};
    const educationTotal = (metrics.patientEducationSetupMet || 0) + (metrics.patientEducationSetupNotMet || 0);
    const educationPercentage = educationTotal > 0
      ? Math.round(((metrics.patientEducationSetupMet || 0) / educationTotal) * 100)
      : 0;

    return [
      {
        key: "technical",
        shortLabel: "Technical",
        title: "Technical Component (Adherence-Based)",
        subtitle: "Device setup, readings capture, and monitoring transmission readiness.",
        codes: ["99445", "99454"],
        percentage: metrics.technical,
        metCount: metrics.technicalMet,
        notMetCount: metrics.technicalNotMet,
        color: COLORS.blue,
        accentClass: "technical"
      },
      {
        key: "professional",
        shortLabel: "Professional",
        title: "Professional Component (Time-Based)",
        subtitle: "Care-team time, review, and patient interaction documentation readiness.",
        codes: RPM_CLINICAL_DISPLAY_CPT_CODES,
        percentage: metrics.professional,
        metCount: metrics.professionalMet,
        notMetCount: metrics.professionalNotMet,
        color: COLORS.purple,
        accentClass: "professional"
      },
      {
        key: "patientEducationSetup",
        shortLabel: "Education",
        title: patientEducationSetup.title || "Patient Education & Setup",
        subtitle: patientEducationSetup.description || "One-time code used for patient education and device setup.",
        codes: [patientEducationSetup.code || "99453"],
        percentage: metrics.patientEducationSetup || educationPercentage,
        metCount: metrics.patientEducationSetupMet,
        notMetCount: metrics.patientEducationSetupNotMet,
        color: "#f97316",
        accentClass: "patient-education-setup"
      }
    ].map((component) => ({
      ...component,
      total: component.metCount + component.notMetCount
    }));
  };

  /* KPI header cards (Total Patients / Fully Billable / Not Billable) — hidden per product request
  const getFullyBillableRules = () => {
    const billingRules = cptCodeDashboardData?.summary?.billingRules || cptCodeDashboardData?.billingRules || {};
    const technicalRule = billingRules?.technical || "Only 1 Technical CPT per month";
    const professionalRule = billingRules?.professional || "Only 1 Professional CPT per month";
    const educationRule = billingRules?.cpt99453 || "Only in first month and only when a technical component is met";
    return [
      technicalRule,
      professionalRule,
      `99453: ${educationRule}`
    ];
  };
  */

  const resolveCptDetailsLayout = () => {
    const rpmClinicalCodes = new Set(RPM_CLINICAL_DISPLAY_CPT_CODES);
    const ccmClinicalCodes = new Set(CCM_CLINICAL_DISPLAY_CPT_CODES);
    const technicalCodes = new Set(TECHNICAL_DISPLAY_CPT_CODES);
    if (selectedComponentKey === "technical") {
      return CPT_DETAILS_LAYOUT_TECHNICAL;
    }
    if (selectedComponentKey === "professional") {
      return CPT_DETAILS_LAYOUT_RPM_CLINICAL;
    }
    if (selectedComponentKey === "ccmClinical") {
      return CPT_DETAILS_LAYOUT_CCM_CLINICAL;
    }
    if (selectedCPTCode && technicalCodes.has(String(selectedCPTCode))) {
      return CPT_DETAILS_LAYOUT_TECHNICAL;
    }
    if (selectedCPTCode && rpmClinicalCodes.has(String(selectedCPTCode))) {
      return CPT_DETAILS_LAYOUT_RPM_CLINICAL;
    }
    if (selectedCPTCode && ccmClinicalCodes.has(String(selectedCPTCode))) {
      return CPT_DETAILS_LAYOUT_CCM_CLINICAL;
    }
    return "default";
  };

  const getPatientListContextMeta = () => {
    const cptDetailsLayout = resolveCptDetailsLayout();

    if (selectedComponentKey) {
      const component = getComponentCardConfigs().find((item) => item.key === selectedComponentKey);

      return {
        badgeLabel: component?.shortLabel || "COMP",
        title: component?.title || "Component Patient List",
        description: component?.subtitle || "",
        supportingBadge: component?.codes?.join(" / ") || "",
        badgeColor: component?.color || COLORS.blue,
        filePrefix: component?.title?.toLowerCase().replace(/\s+/g, "-") || "component-patients",
        emptyLabel: component?.title || "selected component",
        cptDetailsLayout
      };
    }

    const cptCodes = cptCodeDashboardData?.summary?.cptCodes || [];
    const selectedCPTDetails = cptCodes.find((c) => c.code === selectedCPTCode) || {};
    const cptBadgeColors = {
      99439: "#8b5cf6",
      99445: "#10b981",
      99453: "#f97316",
      99454: "#ef4444",
      99457: "#7c3aed",
      99458: "#ec4899",
      99470: "#14b8a6",
      99490: "#84cc17"
    };

    return {
      badgeLabel: selectedCPTCode,
      title: selectedCPTDetails.title || selectedCPTDetails.name || "",
      description: selectedCPTDetails.subtitle || selectedCPTDetails.description || "",
      supportingBadge: selectedCPTDetails.programType || "",
      badgeColor: cptBadgeColors[selectedCPTCode] || "#0ea5e9",
      filePrefix: selectedCPTCode || "cpt-code",
      emptyLabel: `CPT Code ${selectedCPTCode || "-"}`,
      cptDetailsLayout
    };
  };

  const renderPatientListLoading = () => (
    <div className="patient-list-loading">
      {[1, 2, 3, 4, 5].map((row) => (
        <div className="patient-list-loading-row" key={row}>
          <div className="patient-list-loading-cell patient wide patient-profile">
            <div className="patient-list-loading-avatar"></div>
            <div className="patient-list-loading-profile-content">
              <div className="patient-list-loading-cell line-lg"></div>
              <div className="patient-list-loading-cell line-md"></div>
              <div className="patient-list-loading-cell line-sm"></div>
            </div>
          </div>
          <div className="patient-list-loading-cell date line-sm"></div>
          <div className="patient-list-loading-cell date line-sm"></div>
          <div className="patient-list-loading-cell main-provider line-md"></div>
          <div className="patient-list-loading-cell organization line-md"></div>
          <div className="patient-list-loading-cell insurance line-sm"></div>
          <div className="patient-list-loading-cell program line-sm"></div>
          <div className="patient-list-loading-cell badge-cell line-xs"></div>
          <div className="patient-list-loading-cell details line-lg"></div>
          <div className="patient-list-loading-cell action line-btn"></div>
        </div>
      ))}
    </div>
  );

  const renderPatientListModal = () => {
    const showPatientListLoading = ongoingCptCodePatientListRequest || isPatientListFetching;

    const currentData = cptCodePatientListData;
    const rawPatients = currentData?.patients || currentData?.rows || currentData?.list || currentData?.items || currentData?.data || [];
    const normalizedPatientCount = Array.isArray(rawPatients) ? rawPatients.length : 0;
    const totalApiPatients = currentData?.total || currentData?.count || normalizedPatientCount;
    const displayMonth = getDisplayMonth();
    const contextMeta = getPatientListContextMeta();

    return (
      <CommonPatientListModal
        isOpen={patientListModal}
        onToggle={closePatientList}
        contextMeta={contextMeta}
        statusLabel={getPatientListStatusLabel()}
        displayMonth={displayMonth}
        totalApiPatients={totalApiPatients}
        rawPatients={rawPatients}
        showPatientListLoading={showPatientListLoading}
        renderLoading={renderPatientListLoading}
        onViewPatientProfile={handleViewPatientProfile}
      />
    );
  };

  /* KPI header cards (Total Patients / Fully Billable / Not Billable) — hidden per product request
  const renderStatCards = () => {
    const metrics = getComponentMetrics();

    return (
      <Row className="mb-4">
        <Col md="4">
          <Card className="stat-card-simple">
            <CardBody>
              <div className="stat-content">
                <span className="stat-label">Total Patients</span>
                <h2 className="stat-number">{metrics.totalPatients}</h2>
              </div>
              <div className="stat-icon-wrap blue">
                <i className="mdi mdi-account-group"></i>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="stat-card-simple">
            <CardBody>
              <div className="stat-content">
                <span className="stat-label stat-label-with-info">
                  Fully Billable
                  <span className="billable-rule-tooltip-wrap">
                    <i className="mdi mdi-information-outline billable-rule-info-icon"></i>
                    <span className="billable-rule-tooltip-text">
                      <strong>Billing Rules</strong>
                      {getFullyBillableRules().map((rule, index) => (
                        <span className="billable-rule-tooltip-line" key={index}>
                          {rule}
                        </span>
                      ))}
                    </span>
                  </span>
                </span>
                <h2 className="stat-number text-success">{metrics.fullyBillable}</h2>
              </div>
              <div className="stat-icon-wrap green">
                <i className="mdi mdi-check-circle"></i>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="stat-card-simple">
            <CardBody>
              <div className="stat-content">
                <span className="stat-label">Not Billable</span>
                <h2 className="stat-number text-danger">{metrics.notBillable}</h2>
              </div>
              <div className="stat-icon-wrap red">
                <i className="mdi mdi-close-circle"></i>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderCombinedKPICards = () => {
    const metrics = getComponentMetrics();

    return (
      <Row className="mb-4">
        <Col md="4">
          <Card className="stat-card-simple rpm-kpi-card">
            <CardBody>
              <div className="stat-content">
                <span className="stat-label">Total Patients</span>
                <h2 className="stat-number">{metrics.totalPatients}</h2>
              </div>
              <div className="stat-icon-wrap blue">
                <i className="mdi mdi-account-group"></i>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="stat-card-simple rpm-kpi-card">
            <CardBody>
              <div className="stat-content">
                <span className="stat-label">Fully Billable</span>
                <h2 className="stat-number text-success">{metrics.fullyBillable} <span className="kpi-percentage">({metrics.fullyBillablePercentage}%)</span></h2>
              </div>
              <div className="stat-icon-wrap green">
                <i className="mdi mdi-check-circle"></i>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md="4">
          <Card className="stat-card-simple rpm-kpi-card">
            <CardBody>
              <div className="stat-content">
                <span className="stat-label">Not Billable</span>
                <h2 className="stat-number text-danger">{metrics.notBillable} <span className="kpi-percentage">({metrics.notBillablePercentage}%)</span></h2>
              </div>
              <div className="stat-icon-wrap red">
                <i className="mdi mdi-close-circle"></i>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };
  */

  const renderKpiSubLabel = (subLabel) => {
    if (!subLabel) return null;

    const normalized = typeof subLabel === "string" ? { body: subLabel } : subLabel;
    const { heading, body, formula } = normalized;
    const detail = formula || body;

    if (!heading && !detail) return null;

    return (
      <div className="component-performance-child-kpi-sublabel component-performance-child-kpi-sublabel-top">
        <span className="component-performance-kpi-sublabel-line">
          {heading && <span className="component-performance-kpi-sublabel-heading">{heading}</span>}
          {heading && detail && <span className="component-performance-kpi-sublabel-separator">:</span>}
          {detail && <span className="component-performance-kpi-sublabel-body">{detail}</span>}
        </span>
      </div>
    );
  };

  const renderComponentPerformanceChart = () => {
    const metrics = getComponentMetrics();
    const summary = cptCodeDashboardData?.summary || {};
    const cptCodes = cptCodeDashboardData?.summary?.cptCodes || [];
    const selectedMoment = moment(`${selectedYear}-${selectedMonth}-01`);
    const previousMoment = selectedMoment.clone().subtract(1, "month");
    const selectedMonthCardLabel = selectedMoment.format("MMMM YYYY");
    const previousMonthCardLabel = previousMoment.format("MMMM YYYY");
    const cptByCode = cptCodes.reduce((acc, item) => {
      acc[item.code] = item;
      return acc;
    }, {});
    const previousMonthCptCodes = cptCodeDashboardData?.summary?.previousMonthCptCodes || [];
    const previousMonthByCode = previousMonthCptCodes.reduce((acc, item) => {
      acc[item.code] = item;
      return acc;
    }, {});

    const getCodeData = (code, metaLabel = "", source = "current") => {
      const normalizedCode = String(code).trim();
      const sourceMap = source === "previous" ? previousMonthByCode : cptByCode;
      const entry = sourceMap[normalizedCode] || {};
      const currentMeta = cptByCode[normalizedCode] || {};
      const metCount = Number(entry.metCount || 0);
      const notMetCount = Number(entry.notMetCount || 0);
      const total = metCount + notMetCount;
      const percentage = total > 0 ? Math.round((metCount / total) * 100) : 0;
      const fallbackMeta = CPT_PERFORMANCE_CODE_META[normalizedCode] || {};

      return {
        code: normalizedCode,
        label: `CPT ${normalizedCode}`,
        metaLabel,
        metCount,
        notMetCount,
        total,
        percentage,
        title: currentMeta.title || currentMeta.name || fallbackMeta.title || `CPT Code ${code}`,
        description: currentMeta.subtitle || currentMeta.description || fallbackMeta.description || ""
      };
    };

    const getRpmClinicalRow = (code) => {
      const row = getCodeData(code);
      const rpmClinicalService = summary?.rpmClinicalServices || {};
      const metCount = Number(
        rpmClinicalService[`${code}Met`] ??
        cptByCode[code]?.metCount ??
        row.metCount ??
        0
      );
      const notMetCount = Number(
        rpmClinicalService[`${code}NotMet`] ??
        cptByCode[code]?.notMetCount ??
        row.notMetCount ??
        0
      );
      const total = metCount + notMetCount;

      return {
        ...row,
        metCount,
        notMetCount,
        total,
        percentage: total > 0 ? Math.round((metCount / total) * 100) : 0
      };
    };

    const getRowsAggregate = (rows = []) => {
      const met = rows.reduce((sum, row) => sum + Number(row.metCount || 0), 0);
      const notMet = rows.reduce((sum, row) => sum + Number(row.notMetCount || 0), 0);
      return {
        met,
        notMet,
        total: met + notMet
      };
    };

    const rpmTechnicalRows = ["99445", "99454"].map(getCodeData);
    const rpmClinicalRows = RPM_CLINICAL_DISPLAY_CPT_CODES.map(getRpmClinicalRow);
    const rpmClinicalKpis = getRowsAggregate(
      RPM_CLINICAL_COMBINED_MET_CODES.map(getRpmClinicalRow)
    );
    const ccmClinicalRows = ["99490", "99439"].map(getCodeData);
    const newEnrollmentCode = String(summary?.newPatientEnrollmentsCompleted?.currentMonth?.cptCode || "99453");
    const newEnrollmentTitle = cptByCode[newEnrollmentCode]?.title || cptByCode[newEnrollmentCode]?.name || `CPT Code ${newEnrollmentCode}`;
    const newEnrollmentRows = [
      {
        ...getCodeData(newEnrollmentCode),
        monthContext: "current",
        monthLabel: selectedMonthCardLabel,
        metaLabel: selectedMonthCardLabel,
        periodLabel: "Current Month",
        title: newEnrollmentTitle,
        metCount: Number(summary?.newPatientEnrollmentsCompleted?.currentMonth?.metCount ?? cptByCode[newEnrollmentCode]?.metCount ?? 0),
        notMetCount: 0,
        total: Number(summary?.newPatientEnrollmentsCompleted?.currentMonth?.metCount ?? cptByCode[newEnrollmentCode]?.metCount ?? 0),
        percentage: 100
      },
      {
        ...getCodeData(newEnrollmentCode, "", "previous"),
        monthContext: "past",
        monthLabel: previousMonthCardLabel,
        metaLabel: previousMonthCardLabel,
        periodLabel: "Previous Month",
        title: newEnrollmentTitle,
        metCount: Number(summary?.newPatientEnrollmentsCompleted?.pastMonth?.metCount ?? previousMonthByCode[newEnrollmentCode]?.metCount ?? 0),
        notMetCount: 0,
        total: Number(summary?.newPatientEnrollmentsCompleted?.pastMonth?.metCount ?? previousMonthByCode[newEnrollmentCode]?.metCount ?? 0),
        percentage: 100
      }
    ];
    const ccmClinicalKpis = {
      met: Number(metrics.ccmClinicalMet || getRowsAggregate(ccmClinicalRows).met || 0),
      notMet: Number(metrics.ccmClinicalNotMet || getRowsAggregate(ccmClinicalRows).notMet || 0),
      total: Number(metrics.ccmClinicalTotal || getRowsAggregate(ccmClinicalRows).total || 0)
    };
    const serviceGroups = [
      {
        key: "technical",
        isCombinedGroup: true,
        badge: "RPM",
        color: "#0ea5e9",
        icon: "mdi mdi-tools",
        title: "RPM Technical Services",
        description: "Combined technical KPI based on CPT 99445 and 99454.",
        kpiSubLabels: {
          met: {
            heading: "Total Met",
            formula: `99445 (${metrics.technical99445Met}) + 99454 (${metrics.technical99454Met})`
          },
          total: {
            heading: "Total",
            body: "Only patients enrolled in RPM"
          }
        },
        childKpis: {
          met: Number(metrics.technicalMet || 0),
          notMet: Number(metrics.technicalNotMet || 0),
          total: Number(metrics.technicalTotal || ((metrics.technicalMet || 0) + (metrics.technicalNotMet || 0)))
        },
        rows: rpmTechnicalRows
      },
      {
        key: "professional",
        isCombinedGroup: true,
        badge: "RPM",
        color: "#7c3aed",
        icon: "mdi mdi-stethoscope",
        title: "RPM Clinical Services",
        kpiSubLabels: {
          met: {
            heading: "Total Met",
            formula: `99470 (${metrics.professional99470Met}) + 99457 (${metrics.professional99457Met}) = ${rpmClinicalKpis.met}`
          },
          total: {
            heading: "Total",
            body: "Only patients enrolled in RPM"
          }
        },
        combinedMetCptCodes: RPM_CLINICAL_COMBINED_MET_CODES,
        childKpis: {
          met: Number(rpmClinicalKpis.met || metrics.professionalMet || 0),
          notMet: Number(metrics.professionalNotMet || 0),
          total: Number(metrics.professionalTotal || 0)
        },
        rows: rpmClinicalRows
      },
      {
        key: "ccmClinical",
        isCombinedGroup: true,
        badge: "CCM",
        color: "#8b5cf6",
        icon: "mdi mdi-heart-pulse",
        title: "CCM Clinical Services",
        kpiSubLabels: {
          met: {
            heading: "Total Met",
            formula: `99490 (${metrics.ccm99490Met})`
          },
          total: {
            heading: "Total",
            body: "Only patients enrolled in CCM"
          }
        },
        childKpis: ccmClinicalKpis,
        rows: ccmClinicalRows
      },
      {
        key: "patientEducationSetup",
        badge: "NEW",
        color: "#f97316",
        icon: "mdi mdi-account-plus",
        title: "New Patient Enrollments Completed",
        isEnrollmentGroup: true,
        enrollmentCptCode: newEnrollmentCode,
        rows: newEnrollmentRows
      }
    ];

    return (
      <Card className="chart-card-modern">
        <CardBody>
          <div className="chart-header-modern">
            <div className="chart-icon-modern">
              <i className="mdi mdi-view-grid"></i>
            </div>
            <div className="chart-header-copy-modern">
              <h4>Component Performance</h4>
              <p>Individual CPT card pattern grouped by service area with clickable MET and NOT MET counts.</p>
            </div>
          </div>
          <div className="component-performance-grid">
            {serviceGroups.map((group) => (
              <div className="component-performance-card-modern" key={group.title}>
                <div className="component-performance-card-header">
                  <div className="component-performance-header-left">
                    <span className="component-performance-header-icon" style={{ color: group.color }}>
                      <i className={group.icon}></i>
                    </span>
                    <h5>{group.title}</h5>
                  </div>
                </div>
                {group.childKpis && !group.isEnrollmentGroup && (
                  <div className="component-performance-child-kpis">
                        <button
                          type="button"
                          className={`component-performance-child-kpi met component-performance-kpi-button${
                            group.kpiSubLabels?.met ? " has-kpi-sublabel" : ""
                          }`}
                          onClick={() => {
                            if (group.isCombinedGroup) {
                              openComponentPatientList(
                                group.key,
                                "met",
                                group.combinedMetCptCodes ||
                                  (group.rows || []).map((row) => row.code).filter(Boolean)
                              );
                              return;
                            }
                            if (group.rows?.[0]?.code) {
                              openPatientList(group.rows[0].code, "met");
                            }
                          }}
                        >
                          {renderKpiSubLabel(group.kpiSubLabels?.met)}
                          <div className="component-performance-child-kpi-main">
                            <span className="component-performance-child-kpi-label">
                              <i className="mdi mdi-account-check"></i> Met
                            </span>
                            <span className="component-performance-child-kpi-value">{group.childKpis.met}</span>
                            <span className="component-performance-child-kpi-arrow">
                              <i className="mdi mdi-chevron-right"></i>
                            </span>
                          </div>
                        </button>
                        <button
                          type="button"
                          className={`component-performance-child-kpi not-met component-performance-kpi-button${
                            group.kpiSubLabels?.notMet ? " has-kpi-sublabel" : ""
                          }`}
                          onClick={() => {
                            if (group.isCombinedGroup) {
                              openComponentPatientList(
                                group.key,
                                "notMet",
                                group.combinedMetCptCodes ||
                                  (group.rows || []).map((row) => row.code).filter(Boolean)
                              );
                              return;
                            }
                            if (group.rows?.[0]?.code) {
                              openPatientList(group.rows[0].code, "notMet");
                            }
                          }}
                        >
                          {renderKpiSubLabel(group.kpiSubLabels?.notMet)}
                          <div className="component-performance-child-kpi-main">
                            <span className="component-performance-child-kpi-label">
                              <i className="mdi mdi-account-cancel"></i> Not Met
                            </span>
                            <span className="component-performance-child-kpi-value">{group.childKpis.notMet}</span>
                            <span className="component-performance-child-kpi-arrow">
                              <i className="mdi mdi-chevron-right"></i>
                            </span>
                          </div>
                        </button>
                        <div className={`component-performance-child-kpi total${group.kpiSubLabels?.total ? " has-kpi-sublabel" : ""}`}>
                          {renderKpiSubLabel(group.kpiSubLabels?.total)}
                          <div className="component-performance-child-kpi-main">
                            <span className="component-performance-child-kpi-label">
                              <i className="mdi mdi-account-group"></i> Total
                            </span>
                            <span className="component-performance-child-kpi-value">{group.childKpis.total}</span>
                          </div>
                        </div>
                  </div>
                )}
                <div className={`component-performance-cpt-list ${group.rows.length >= 3 ? "component-performance-cpt-list-triple" : ""}${group.isEnrollmentGroup ? " component-performance-cpt-list-enrollment" : ""}`}>
                  {group.rows.map((row) => (
                    <div
                      className={`component-performance-cpt-card${
                        row.periodLabel
                          ? ` enrollment-cpt-card${
                              row.monthContext === "past" ? " enrollment-cpt-card-previous" : " enrollment-cpt-card-current"
                            }`
                          : ""
                      }`}
                      key={`${group.title}-${row.code}-${row.metaLabel || "default"}`}
                    >
                      {row.periodLabel && (
                        <div className="component-performance-enrollment-card-label">
                          <span className="component-performance-enrollment-card-label-title">
                            <i
                              className={`mdi ${
                                row.monthContext === "past" ? "mdi-calendar-clock" : "mdi-calendar-today"
                              }`}
                            ></i>
                            {row.periodLabel}
                          </span>
                          {row.monthLabel && (
                            <span className="component-performance-enrollment-card-label-month">{row.monthLabel}</span>
                          )}
                        </div>
                      )}
                      <div className="component-performance-cpt-header">
                        <div>
                          <div className="component-performance-cpt-code">{row.label}</div>
                          {row.kpiSubLabel ? (
                            <div className="component-performance-cpt-meta">
                              {renderKpiSubLabel(row.kpiSubLabel)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="component-performance-cpt-title">{row.title}</div>
                      <div className="component-performance-kpi-strip">
                        <div className="component-performance-kpi-strip-left">
                          <span className="component-performance-kpi-tag">MET</span>
                          <span className="component-performance-kpi-count">
                            {row.metCount}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-modern met component-performance-kpi-action-btn"
                          onClick={() => openPatientList(row.code, "met", row.monthContext || null)}
                        >
                          <i className="mdi mdi-account-check"></i> View MET Patients
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    );
  };

  const loading = ongoingCptCodeDashboardRequest;
  const isBusy = loading || isFiltering;
  const showSkeleton = (loading && !cptCodeDashboardData) || isFiltering;
  const isRefetching = loading && cptCodeDashboardData && !isFiltering;

  return (
    <div
      className={`cpt-performance-wrapper cpt-dash-report-surface ${
        isRefetching ? "cpt-dash-report-surface--busy" : ""
      }`}
    >
      <IntegrationFilter
        onFilterChange={handleFilterChange}
        onApply={handleFilterApply}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        currentMonth={getDisplayMonth()}
        loading={isBusy}
      />

      {showSkeleton ? (
        <CPTCodePerformanceShimmer />
      ) : (
        <>
          {isRefetching && (
            <CptDashboardSkeletonOverlay>
              <CPTCodePerformanceShimmer />
            </CptDashboardSkeletonOverlay>
          )}

      <div className="content-section content-visible">
        <div className="data-enter-animation" key={dataKey}>
          {/* {renderStatCards()} */}

            <Row className="mb-4">
              <Col md="12">
                {renderComponentPerformanceChart()}
              </Col>
            </Row>

          </div>
        </div>
        {renderPatientListModal()}
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ careprovider, user }) => ({
  cptCodeDashboardData: careprovider.cptCodeDashboardData,
  ongoingCptCodeDashboardRequest: careprovider.ongoingCptCodeDashboardRequest,
  cptCodeDashboardError: careprovider.cptCodeDashboardError,
  cptCodePatientListData: careprovider.cptCodePatientListData,
  ongoingCptCodePatientListRequest: careprovider.ongoingCptCodePatientListRequest,
  cptCodePatientListError: careprovider.cptCodePatientListError,
  componentPatientsData: careprovider.componentPatientsData,
  ongoingComponentPatientsRequest: careprovider.ongoingComponentPatientsRequest,
  componentPatientsError: careprovider.componentPatientsError
});

export default connect(mapStateToProps)(CPTCodePerformance);